import { nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useConsentStore } from '~/stores/consent'
import { initAttribution, syncAttributionConsent } from '~/utils/attribution'
import { initGtmContainer, pushToDataLayer, updateGoogleConsent } from '~/utils/gtm'
import { installContactClickTracking } from '~/utils/tracking-contact'
import { sanitizeUrl } from '~/utils/tracking-pii'
import { track } from '~/utils/tracking'

export default defineNuxtPlugin(async (nuxtApp) => {
  if (import.meta.server) return

  const consent = useConsentStore()
  const router = useRouter()

  initAttribution(consent.state)

  // Advanced Consent Mode: the container always loads. initGtmContainer() sets the
  // denied default before gtm.js, and the watcher below only ever sends updates.
  const { gtmId, siteEnv } = useRuntimeConfig().public
  if (gtmId) initGtmContainer(gtmId, siteEnv)

  // `updateGoogleConsent` is deduped, so the banner and the privacy page can also call
  // it eagerly for the same state change without producing a second `consent update`.
  watch(() => consent.state, (state, previous) => {
    updateGoogleConsent(state === 'accepted')
    syncAttributionConsent(state)
    // Lets GTM start the consent-gated tags (Meta PageView, Clarity) on the page where the
    // visitor accepted, instead of waiting for the next page_view.
    if (state === 'accepted' && previous !== undefined && previous !== 'accepted') track('consent_granted')
  }, { immediate: true })

  installContactClickTracking()

  // The Google tag reads page_location / page_referrer from these sanitized values for
  // every event, so an email typed into the search never reaches GA4 or Google Ads.
  let previousLocation = document.referrer
  function pageContext() {
    const location = window.location.href
    const pageLocation = sanitizeUrl(location)
    const sanitized = new URL(pageLocation)
    const context = {
      page_location: pageLocation,
      page_referrer: sanitizeUrl(previousLocation),
      page_path: sanitized.pathname + sanitized.search,
    }
    previousLocation = location
    return context
  }

  await router.isReady()
  nextTick(() => track('page_view', { ...pageContext(), page_title: document.title }))

  // Nuxt updates <title> only once the new page has rendered (page:finish), while the
  // page's own events (view_item, view_item_list) may fire before that. So the new
  // location goes to the dataLayer right away, and page_view follows with the new title.
  let navigation = 0
  router.afterEach((to, from) => {
    if (to.fullPath === from.fullPath) return
    const current = ++navigation
    const context = pageContext()
    pushToDataLayer(context)
    afterPageRendered().then(() => {
      if (current === navigation) track('page_view', { ...context, page_title: document.title })
    })
  })

  function afterPageRendered(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unhook()
        // The head is synced inside page:finish; a macrotask later the title is in the DOM.
        setTimeout(resolve, 0)
      }
      const unhook = nuxtApp.hook('page:finish', finish)
      // A query-only change on the same page may not suspend, so page:finish never comes.
      const timer = setTimeout(finish, 1000)
    })
  }
})
